var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable, reaction } from "mobx";
export const NOT_LOADED = Symbol("NOT_LOADED");
export const EMPTY_INVALIDATOR = {
    onInvalidate: () => {
    },
};
class BaseRemoteData {
    constructor(invalidator) {
        this.internalValue = NOT_LOADED;
        this.loading = false;
        this.loadingPromise = null;
        this.loadQueued = false;
        this.forceReloadQueued = false;
        this.invalidated = false;
        invalidator.onInvalidate(async () => {
            this.invalidated = true;
            if (this.loadingPromise)
                await this.loadingPromise.catch(() => { });
            this.invalidated = false;
            this.internalValue = NOT_LOADED;
        });
    }
    get value() {
        this.load();
        return this.internalValue;
    }
    async waitForValue() {
        if (this.loading && this.loadingPromise) {
            await this.loadingPromise;
        }
        else if (this.internalValue === NOT_LOADED) {
            await this.doLoad(false);
        }
        return this.internalValue;
    }
    load() {
        if (this.internalValue === NOT_LOADED && !this.loading) {
            this.queueLoad(false);
        }
    }
    reload() {
        if (this.internalValue !== NOT_LOADED) {
            this.queueLoad(true);
        }
    }
    async waitForReload() {
        if (this.internalValue !== NOT_LOADED) {
            await this.doLoad(true);
        }
    }
    queueLoad(forceReload) {
        this.forceReloadQueued = this.forceReloadQueued || forceReload;
        if (this.loadQueued)
            return;
        this.loadQueued = true;
        setTimeout(() => {
            this.loadQueued = false;
            const shouldForceReload = this.forceReloadQueued;
            this.forceReloadQueued = false;
            void this.doLoad(shouldForceReload);
        }, 0);
    }
    async doLoad(forceReload) {
        if (!forceReload && this.internalValue !== NOT_LOADED)
            return;
        while (this.loadingPromise) {
            await this.loadingPromise.catch(() => { });
            if (!forceReload)
                return;
        }
        this.loadingPromise = (async () => {
            this.loading = true;
            try {
                const internalValue = await this.loadImpl();
                if (!this.invalidated) {
                    this.internalValue = internalValue;
                }
            }
            finally {
                this.loading = false;
                this.loadingPromise = null;
            }
        })();
        await this.loadingPromise;
    }
}
__decorate([
    observable
], BaseRemoteData.prototype, "internalValue", void 0);
export class RemoteData extends BaseRemoteData {
    constructor(invalidator, loader) {
        super(invalidator);
        this.loader = loader;
    }
    async loadImpl() {
        return this.loader();
    }
}
export class ParameterizedRemoteData extends BaseRemoteData {
    constructor(invalidator, observableInput, loader) {
        super(invalidator);
        this.observableInput = observableInput;
        this.loader = loader;
        reaction(() => this.input, () => this.reload());
    }
    get input() {
        return this.observableInput();
    }
    async loadImpl() {
        return await this.loader(this.input);
    }
}
__decorate([
    computed
], ParameterizedRemoteData.prototype, "input", null);
export class ParameterizedPagedRemoteData extends BaseRemoteData {
    constructor(invalidator, pageSize, observableInput, loader) {
        super(invalidator);
        this.pageSize = pageSize;
        this.observableInput = observableInput;
        this.loader = loader;
        this.total = 0;
        this.currentPage = 0;
        reaction(() => this.input, () => this.reload());
    }
    get input() {
        return this.observableInput();
    }
    get numberOfPages() {
        return Math.ceil(this.total / this.pageSize);
    }
    async loadImpl() {
        const response = await this.loader({
            ...this.input,
            start: this.currentPage * this.pageSize,
            end: this.currentPage * this.pageSize + this.pageSize,
        });
        this.total = response.total;
        return response.results;
    }
    setCurrentPage(currentPage) {
        this.currentPage = currentPage;
    }
}
__decorate([
    observable
], ParameterizedPagedRemoteData.prototype, "total", void 0);
__decorate([
    observable
], ParameterizedPagedRemoteData.prototype, "currentPage", void 0);
__decorate([
    computed
], ParameterizedPagedRemoteData.prototype, "input", null);
__decorate([
    computed
], ParameterizedPagedRemoteData.prototype, "numberOfPages", null);
export function checkLoadedProperties(object, ...properties) {
    return properties.every((property) => object[property] !== NOT_LOADED);
}
export function makeCheckLoaded(clazz, ...properties) {
    return (object) => {
        return properties.every((property) => object[property] !== NOT_LOADED);
    };
}
